/**
 * marketingScripts — consent-gated loader for Zoho SalesIQ (chat widget)
 * and Zoho Marketing Automation (PageSense tracking).
 *
 * Both are tracking / marketing tools and MUST NOT load on page render. They
 * only inject after the user grants the analytics-category consent in the
 * cookie banner (see `components/CookieConsent.jsx`). GTM is consent-gated
 * at the *signal* level via Google Consent Mode v2 (loads always, but
 * receives `analytics_storage: denied` until granted). These Zoho scripts
 * are gated at the *load* level — no signal-mode equivalent on the Zoho
 * side, so we keep them out of the document entirely until consent.
 *
 * Idempotent: re-calls are no-ops once the scripts are injected. On consent
 * withdrawal, the documented SalesIQ tracking.off() API is requested when
 * its ready handler is available. No equivalent PageSense teardown is
 * confirmed, so PageSense remains an explicit verification hold.
 *
 * react-snap safety: useEffect-triggered, so this never runs during the
 * static prerender pass (puppeteer has no consent cookie anyway).
 *
 * Host gate (staging isolation, Sep 2026): the collector IDs below are the
 * PRODUCTION PageSense project and SalesIQ widget, so they only load on the
 * production hostnames (see infra/certs.tf). Staging
 * (d2hqz144sz4ut1.cloudfront.net), localhost and any preview host never
 * inject either script or create the $zoho stub — in every path, because
 * all of them (stored consent on mount, banner accept, preference update)
 * go through loadMarketingScripts(). Route changes only toggle an
 * already-loaded widget (SalesIQController) and are no-ops without it.
 * Give staging its own approved test projects before loading anything there.
 */

const ZOHO_PRODUCTION_HOSTS = ["candopayments.com", "www.candopayments.com"];

export function isZohoHostAllowed(hostname) {
  const host = hostname ?? (typeof window !== "undefined" ? window.location.hostname : "");
  return ZOHO_PRODUCTION_HOSTS.includes(String(host).toLowerCase());
}

let scriptsLoaded = false;
let salesIqTrackingOffRequested = false;

const SALESIQ_SCRIPT_ID = "zsiqscript";
const SALESIQ_SRC =
  "https://salesiq.zohopublic.eu/widget?wc=siqa3d22a834d66e5b70fb7c670b15db9a4ba0a087bbc8e562ca1fdd0b7b2247718dc07b49452835299c3940c54c4b9ee5f";
const PAGESENSE_SRC =
  "https://cdn-eu.pagesense.io/js/zzob828a/11fe4d682b1c4f6793ac864e93dd07a2.js";

function requestSalesIqTrackingOffWhenReady() {
  if (typeof window === "undefined" || !salesIqTrackingOffRequested) return false;
  const salesiq = window.$zoho && window.$zoho.salesiq;
  if (!salesiq) return false;
  const apply = () => {
    if (!salesIqTrackingOffRequested) return false;
    const tracking = window.$zoho && window.$zoho.salesiq && window.$zoho.salesiq.tracking;
    if (tracking && typeof tracking.off === "function") {
      try { tracking.off(); return true; } catch (_) { /* SDK unavailable during readiness transition */ }
    }
    return false;
  };
  const applied = apply();
  // Do not replace the SDK's ready callback. It owns its lifecycle; this
  // queues a second supported readiness callback for a delayed SDK.
  if (!applied && typeof salesiq.ready === "function") {
    try { salesiq.ready(apply); } catch (_) { /* widget stub or failed SDK */ }
  }
  return true;
}

function injectSalesIQ() {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (!isZohoHostAllowed()) return;
  if (document.getElementById(SALESIQ_SCRIPT_ID)) return;

  // SalesIQ stub — must exist BEFORE the widget loader runs so the
  // widget can attach its real implementation onto the placeholder.
  window.$zoho = window.$zoho || {};
  window.$zoho.salesiq = window.$zoho.salesiq || { ready: function () {} };

  const s = document.createElement("script");
  s.id = SALESIQ_SCRIPT_ID;
  s.src = SALESIQ_SRC;
  s.defer = true;
  s.addEventListener("load", requestSalesIqTrackingOffWhenReady);
  document.head.appendChild(s);
}

function injectPageSense() {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (!isZohoHostAllowed()) return;
  // PageSense uses its own internal de-dup; we also short-circuit on the
  // module-level loaded flag, but a defensive document check protects against
  // any future direct callers.
  const existing = Array.from(document.scripts).find((node) => node.src === PAGESENSE_SRC);
  if (existing) return;

  const e = document.createElement("script");
  e.type = "text/javascript";
  e.async = true;
  e.src = PAGESENSE_SRC;
  const x = document.getElementsByTagName("script")[0];
  if (x && x.parentNode) {
    x.parentNode.insertBefore(e, x);
  } else {
    document.head.appendChild(e);
  }
}

/**
 * Inject SalesIQ + PageSense once. Idempotent — subsequent calls are no-ops.
 * Call this from inside the cookie-consent grant handler (and on app mount
 * when consent is already stored).
 */
export function loadMarketingScripts() {
  if (scriptsLoaded) return;
  // Non-production host: never inject the production Zoho collectors.
  if (!isZohoHostAllowed()) return;
  scriptsLoaded = true;
  injectSalesIQ();
  injectPageSense();
}

/**
 * Stop future SalesIQ tracking after analytics consent withdrawal.
 *
 * This invokes the documented SalesIQ `tracking.off()` through the current
 * SDK or its ready callback. It intentionally does not remove scripts or
 * claim a PageSense teardown, and it does not re-enable tracking on a later
 * grant because a corresponding documented `tracking.on()` contract has not
 * been verified in this workstream.
 */
export function withdrawMarketingTracking() {
  salesIqTrackingOffRequested = true;
  requestSalesIqTrackingOffWhenReady();
}

/**
 * Set SalesIQ floating-button visibility. Safe to call before the widget
 * has finished loading — defers to `$zoho.salesiq.ready` in that case.
 * Used by `SalesIQController` to hide the widget on /statement-review and
 * /book per the route policy.
 */
export function setSalesIQVisible(visible) {
  if (typeof window === "undefined") return;
  const sq = window.$zoho && window.$zoho.salesiq;
  if (!sq) return;
  const state = visible ? "show" : "hide";
  const apply = () => {
    const fb = window.$zoho && window.$zoho.salesiq && window.$zoho.salesiq.floatbutton;
    if (fb && typeof fb.visible === "function") {
      try { fb.visible(state); } catch (_) { /* widget not fully attached yet */ }
    }
  };
  // Fast path — widget already attached.
  apply();
  // Belt-and-braces — queue inside ready() so the call lands once the widget
  // replaces the stub.
  if (typeof sq.ready === "function") {
    try { sq.ready(apply); } catch (_) { /* stub-only — first apply will run when widget loads */ }
  }
}
